import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  ArrowLeft,
  Send,
  Users,
  Settings,
  Loader2,
  UserPlus,
  UserMinus,
  ExternalLink,
  MessageCircle,
  MessageSquare,
  Phone,
  Trash2,
  MoreVertical,
  Paperclip,
  Image,
  FileText,
  Video,
  X,
  Download,
  SmilePlus,
  Reply
} from 'lucide-react';
import { groupsAPI, notificationsAPI, uploadAPI, presenceAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import notificationSound from '../utils/notificationSound';
import { LinkifyText } from '../utils/linkify';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

// Available reaction emojis
const REACTION_EMOJIS = ['👍', '❤️', '🙏', '😂', '🎉', '👏'];

const GroupChatPage = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [group, setGroup] = useState(null);
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState([]);
  const [joinRequests, setJoinRequests] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [showRequests, setShowRequests] = useState(false);
  const [processingRequest, setProcessingRequest] = useState(null);
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedImage, setSelectedImage] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [onlineCount, setOnlineCount] = useState(0);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const prevMessageCount = useRef(0);
  const isInitialLoad = useRef(true);
  const userScrolledUp = useRef(false);
  const messagesContainerRef = useRef(null);
  const lastScrollPosition = useRef(0);

  useEffect(() => {
    loadGroupData();
    // Poll for new messages every 5 seconds
    const interval = setInterval(loadMessages, 5000);
    // Poll online member count every 20 seconds
    loadPresence();
    const presenceInterval = setInterval(loadPresence, 20000);
    return () => {
      clearInterval(interval);
      clearInterval(presenceInterval);
    };
  }, [groupId]);

  const loadPresence = async () => {
    try {
      const data = await presenceAPI.getGroup(groupId);
      setOnlineCount(data.onlineCount || 0);
    } catch (error) {
      // ignore presence errors
    }
  };

  // Handle scroll position tracking - called on every scroll
  const handleScroll = () => {
    if (!messagesContainerRef.current) return;
    const container = messagesContainerRef.current;
    const { scrollTop, scrollHeight, clientHeight } = container;
    
    // Save current scroll position
    lastScrollPosition.current = scrollTop;
    
    // User has scrolled up if they're more than 150px from the bottom
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    userScrolledUp.current = distanceFromBottom > 150;
  };

  // Restore scroll position after messages update (if user was scrolled up)
  const restoreScrollPosition = () => {
    if (messagesContainerRef.current && userScrolledUp.current) {
      messagesContainerRef.current.scrollTop = lastScrollPosition.current;
    }
  };

  useEffect(() => {
    // Mark notifications as read when opening the chat
    notificationsAPI.markGroupRead(groupId).catch(console.error);
  }, [groupId]);

  const loadGroupData = async () => {
    try {
      const [groupData, messagesData, membersData] = await Promise.all([
        groupsAPI.getOne(groupId),
        groupsAPI.getMessages(groupId),
        groupsAPI.getMembers(groupId)
      ]);
      
      setGroup(groupData);
      setMessages(messagesData);
      setMembers(membersData);
      prevMessageCount.current = messagesData.length;
      
      // Scroll to bottom on initial load
      setTimeout(() => scrollToBottom(), 100);
      
      // Load join requests if user is leader
      if (groupData.isLeader) {
        const requests = await groupsAPI.getJoinRequests(groupId);
        setJoinRequests(requests);
      }
    } catch (error) {
      console.error('Error loading group data:', error);
      if (error.response?.status === 403) {
        navigate('/ministry-teams');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    try {
      const messagesData = await groupsAPI.getMessages(groupId);
      
      // Check if there are new messages
      const hasNewMessages = messagesData.length > prevMessageCount.current;
      
      // Play sound if new messages arrived from others (not initial load)
      if (prevMessageCount.current > 0 && hasNewMessages) {
        // Check if the new message is from someone else
        const newestMessage = messagesData[messagesData.length - 1];
        if (newestMessage && newestMessage.userId !== user?.id) {
          notificationSound.playGroupChatSound();
        }
      }
      
      prevMessageCount.current = messagesData.length;
      
      // If user has scrolled up, save their position before updating messages
      const wasScrolledUp = userScrolledUp.current;
      const savedScrollTop = lastScrollPosition.current;
      
      setMessages(messagesData);
      
      // Handle scrolling based on context
      if (isInitialLoad.current) {
        // First load - scroll to bottom
        isInitialLoad.current = false;
        setTimeout(() => scrollToBottom(), 100);
      } else if (wasScrolledUp) {
        // User was reading old messages - keep their position
        setTimeout(() => {
          if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = savedScrollTop;
          }
        }, 50);
      } else if (hasNewMessages) {
        // User was at bottom and new messages arrived - scroll to show them
        setTimeout(() => scrollToBottom(), 100);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && !attachment) || sending) return;
    
    setSending(true);
    try {
      const replyPayload = replyTo
        ? { id: replyTo.id, userName: replyTo.userName, content: (replyTo.content || '').slice(0, 150) }
        : null;
      await groupsAPI.sendMessage(groupId, newMessage.trim() || (attachment ? 'Sent an attachment' : ''), attachment?.url, attachment?.type, replyPayload);
      setNewMessage('');
      setAttachment(null);
      setReplyTo(null);
      
      // Reset scroll tracking - user just sent a message, so scroll to bottom
      userScrolledUp.current = false;
      
      await loadMessages();
      // Force scroll to bottom after sending own message
      setTimeout(() => scrollToBottom(), 150);
      inputRef.current?.focus();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size on frontend before upload
    const maxVideoSize = 100 * 1024 * 1024; // 100MB for videos
    const maxImageSize = 10 * 1024 * 1024;  // 10MB for images
    const maxDocSize = 25 * 1024 * 1024;    // 25MB for documents
    
    const isVideo = file.type.startsWith('video/');
    const isImage = file.type.startsWith('image/');
    const maxSize = isVideo ? maxVideoSize : isImage ? maxImageSize : maxDocSize;
    
    if (file.size > maxSize) {
      const maxMB = maxSize / (1024 * 1024);
      const fileMB = (file.size / (1024 * 1024)).toFixed(1);
      alert(`File too large (${fileMB}MB). Maximum ${isVideo ? 'video' : isImage ? 'image' : 'document'} size is ${maxMB}MB.`);
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    try {
      const result = await uploadAPI.uploadFile(file, (progress) => {
        setUploadProgress(progress);
      });
      // Cloudinary returns full URLs, local uploads return relative URLs
      const fullUrl = result.url.startsWith('http') 
        ? result.url 
        : `${process.env.REACT_APP_BACKEND_URL}${result.url}`;
      
      // Determine attachment type
      let attachmentType = 'document';
      if (result.type?.startsWith('image/')) {
        attachmentType = 'image';
      } else if (result.type?.startsWith('video/')) {
        attachmentType = 'video';
      }
      
      setAttachment({
        url: fullUrl,
        type: attachmentType,
        name: file.name
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      // Show more helpful error message
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      if (errorMsg.includes('timeout')) {
        alert('Upload timed out. Please try again with a stable internet connection, or try a smaller file.');
      } else if (errorMsg.includes('too large')) {
        alert(errorMsg);
      } else {
        alert(`Failed to upload file: ${errorMsg}`);
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const clearAttachment = () => {
    setAttachment(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getBackendUrl = () => {
    return process.env.REACT_APP_BACKEND_URL || '';
  };

  const getFullUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${getBackendUrl()}${url}`;
  };

  const handleJoinRequest = async (userId, action) => {
    setProcessingRequest(userId);
    try {
      await groupsAPI.handleJoinRequest(groupId, userId, action);
      // Reload requests and members
      const [requests, membersData] = await Promise.all([
        groupsAPI.getJoinRequests(groupId),
        groupsAPI.getMembers(groupId)
      ]);
      setJoinRequests(requests);
      setMembers(membersData);
    } catch (error) {
      console.error('Error handling request:', error);
      alert('Failed to process request');
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this member?')) return;
    
    try {
      await groupsAPI.removeMember(groupId, userId);
      const membersData = await groupsAPI.getMembers(groupId);
      setMembers(membersData);
    } catch (error) {
      console.error('Error removing member:', error);
      alert(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleLeaveGroup = async () => {
    if (!window.confirm('Are you sure you want to leave this group?')) return;
    
    try {
      await groupsAPI.removeMember(groupId, user.id);
      navigate('/ministry-teams');
    } catch (error) {
      console.error('Error leaving group:', error);
      alert(error.response?.data?.detail || 'Failed to leave group');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    
    try {
      await groupsAPI.deleteMessage(groupId, messageId);
      setMessages(messages.filter(m => m.id !== messageId));
    } catch (error) {
      console.error('Error deleting message:', error);
      alert(error.response?.data?.detail || 'Failed to delete message');
    }
  };

  const handleReaction = async (messageId, emoji) => {
    try {
      const result = await groupsAPI.toggleReaction(groupId, messageId, emoji);
      // Update the message reactions in state
      setMessages(messages.map(m => {
        if (m.id === messageId) {
          return { ...m, reactions: result.reactions };
        }
        return m;
      }));
    } catch (error) {
      console.error('Error toggling reaction:', error);
    }
  };

  // Resolve reactor user IDs to readable names for the "reacted by" tooltip
  const getReactorNames = (userIds = []) => {
    return userIds
      .map(id => {
        if (id === user?.id) return 'You';
        const member = members.find(m => m.userId === id);
        return member?.userName || 'A member';
      })
      .join(', ');
  };

  // Check if user can delete a message (own message, leader, admin, or pastor)
  const canDeleteMessage = (message) => {
    const isOwn = message.userId === user?.id;
    const isLeader = group?.isLeader;
    const isAdminOrPastor = user?.role === 'admin' || user?.role === 'pastor';
    return isOwn || isLeader || isAdminOrPastor;
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const isYesterday = new Date(now - 86400000).toDateString() === date.toDateString();
    
    const timeStr = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    
    if (isToday) {
      return `Today at ${timeStr}`;
    } else if (isYesterday) {
      return `Yesterday at ${timeStr}`;
    } else {
      // Show full date with time
      return date.toLocaleDateString([], { 
        month: 'short', 
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      }) + ` at ${timeStr}`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-navy-900" />
      </div>
    );
  }

  if (!group) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p>Group not found</p>
      </div>
    );
  }

  // Members Panel
  if (showMembers) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-br from-navy-900 to-navy-800 text-white px-4 py-4 sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowMembers(false)}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            <div className="flex-1">
              <h1 className="font-bold">{group.name}</h1>
              <p className="text-sm text-tan-400">{members.length} members</p>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-3">
          {members.map((member) => (
            <Card key={member.id} className="border-0 shadow-md rounded-xl">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-navy-900">{member.userName}</p>
                    <Badge 
                      className={member.status === 'leader' 
                        ? 'bg-purple-100 text-purple-700' 
                        : 'bg-green-100 text-green-700'
                      }
                    >
                      {member.status === 'leader' ? 'Leader' : 'Member'}
                    </Badge>
                  </div>
                  {group.isLeader && member.status !== 'leader' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:bg-red-50"
                      onClick={() => handleRemoveMember(member.userId)}
                    >
                      <UserMinus className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
          
          {!group.isLeader && (
            <Button
              variant="outline"
              className="w-full mt-4 text-red-500 border-red-200 hover:bg-red-50"
              onClick={handleLeaveGroup}
            >
              Leave Group
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Join Requests Panel (Leaders only)
  if (showRequests && group.isLeader) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-gradient-to-br from-navy-900 to-navy-800 text-white px-4 py-4 sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowRequests(false)}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            <div className="flex-1">
              <h1 className="font-bold">Join Requests</h1>
              <p className="text-sm text-tan-400">{joinRequests.length} pending</p>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-3">
          {joinRequests.length === 0 ? (
            <Card className="border-0 shadow-md rounded-xl">
              <CardContent className="p-8 text-center">
                <UserPlus className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No pending join requests</p>
              </CardContent>
            </Card>
          ) : (
            joinRequests.map((request) => (
              <Card key={request.id} className="border-0 shadow-md rounded-xl">
                <CardContent className="p-4">
                  <div className="mb-3">
                    <p className="font-medium text-navy-900">{request.userName}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-xs text-gray-500">
                        Requested {formatTime(request.joinedAt)}
                      </p>
                      {request.status === 'conversation_requested' && (
                        <Badge className="bg-amber-100 text-amber-700 text-xs">
                          <Phone className="w-3 h-3 mr-1" />
                          Awaiting Call
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-500 border-red-200 hover:bg-red-50"
                      onClick={() => handleJoinRequest(request.userId, 'deny')}
                      disabled={processingRequest === request.userId}
                      data-testid={`deny-request-btn-${request.userId}`}
                    >
                      Deny
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-amber-600 border-amber-200 hover:bg-amber-50"
                      onClick={() => handleJoinRequest(request.userId, 'request_conversation')}
                      disabled={processingRequest === request.userId || request.status === 'conversation_requested'}
                      data-testid={`talk-first-btn-${request.userId}`}
                    >
                      <MessageSquare className="w-3 h-3 mr-1" />
                      {request.status === 'conversation_requested' ? 'Requested' : 'Talk First'}
                    </Button>
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => handleJoinRequest(request.userId, 'approve')}
                      disabled={processingRequest === request.userId}
                      data-testid={`approve-request-btn-${request.userId}`}
                    >
                      {processingRequest === request.userId ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        'Approve'
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    );
  }

  // Main Chat View
  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-br from-navy-900 to-navy-800 text-white px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/ministry-teams')}
            className="text-white hover:bg-white/10"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-bold truncate">{group.name}</h1>
            <p className="text-sm text-tan-400 flex items-center gap-1.5" data-testid="group-presence-status">
              <span>{members.length} members</span>
              {onlineCount > 0 && (
                <>
                  <span className="w-1 h-1 rounded-full bg-tan-400 inline-block" />
                  <span className="flex items-center gap-1 text-green-400">
                    <span className="w-2 h-2 rounded-full bg-green-400 inline-block" />
                    {onlineCount} online
                  </span>
                </>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {group.isLeader && joinRequests.length > 0 && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowRequests(true)}
                className="text-white hover:bg-white/10 relative"
                data-testid="requests-btn"
              >
                <UserPlus className="w-5 h-5" />
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
                  {joinRequests.length}
                </span>
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowMembers(true)}
              className="text-white hover:bg-white/10"
              data-testid="members-btn"
            >
              <Users className="w-5 h-5" />
            </Button>
            {group.externalLink && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => window.open(group.externalLink, '_blank')}
                className="text-white hover:bg-white/10"
                data-testid="external-link-btn"
              >
                <ExternalLink className="w-5 h-5" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div 
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto p-4 space-y-3"
      >
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 py-12">
            <MessageCircle className="w-16 h-16 mb-4 opacity-50" />
            <p className="text-lg font-medium">No messages yet</p>
            <p className="text-sm">Be the first to say something!</p>
          </div>
        ) : (
          messages.map((message) => {
            const isOwn = message.userId === user?.id;
            return (
              <div
                key={message.id}
                className={`flex ${isOwn ? 'justify-end' : 'justify-start'} group`}
              >
                <div className={`flex items-start gap-1 max-w-[85%] ${isOwn ? 'flex-row-reverse' : ''}`}>
                  <div
                    className={`rounded-2xl px-4 py-2 ${
                      isOwn
                        ? 'bg-navy-900 text-white rounded-br-sm'
                        : 'bg-white text-gray-800 rounded-bl-sm shadow-md'
                    }`}
                  >
                    {!isOwn && (
                      <p className="text-xs font-medium text-tan-600 mb-1">
                        {message.userName}
                      </p>
                    )}
                    {message.replyTo && (
                      <div
                        className={`mb-2 pl-2 border-l-2 rounded-r-md py-1 text-xs ${
                          isOwn ? 'border-tan-400 bg-white/10' : 'border-navy-300 bg-gray-100'
                        }`}
                        data-testid={`group-reply-quote-${message.id}`}
                      >
                        <p className={`font-semibold ${isOwn ? 'text-tan-300' : 'text-navy-700'}`}>
                          {message.replyTo.userName}
                        </p>
                        <p className={`truncate ${isOwn ? 'text-gray-200' : 'text-gray-500'}`}>
                          {message.replyTo.content}
                        </p>
                      </div>
                    )}
                    {/* Attachment Display */}
                    {message.attachmentUrl && (
                      <div className="mb-2">
                        {message.attachmentType === 'image' ? (
                          <img 
                            src={getFullUrl(message.attachmentUrl)} 
                            alt="Attachment" 
                            className="max-w-full max-h-48 rounded-lg cursor-pointer hover:opacity-90"
                            onClick={() => setSelectedImage(getFullUrl(message.attachmentUrl))}
                          />
                        ) : message.attachmentType === 'video' ? (
                          <video 
                            src={getFullUrl(message.attachmentUrl)} 
                            controls 
                            className="max-w-full max-h-48 rounded-lg"
                          />
                        ) : (
                          <a 
                            href={getFullUrl(message.attachmentUrl)} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className={`flex items-center gap-2 p-2 rounded-lg ${isOwn ? 'bg-navy-800' : 'bg-gray-100'}`}
                          >
                            <FileText className="w-5 h-5" />
                            <span className="text-sm underline">View Document</span>
                            <Download className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    )}
                    {message.content && message.content !== 'Sent an attachment' && (
                      <p className="text-sm whitespace-pre-wrap break-words">
                        <LinkifyText>{message.content}</LinkifyText>
                      </p>
                    )}
                    
                    {/* Reactions Display */}
                    {message.reactions && Object.keys(message.reactions).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {Object.entries(message.reactions).map(([emoji, users]) => (
                          <button
                            key={emoji}
                            onClick={() => handleReaction(message.id, emoji)}
                            title={`${getReactorNames(users)} reacted with ${emoji}`}
                            className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs transition-colors ${
                              users.includes(user?.id)
                                ? isOwn 
                                  ? 'bg-navy-700 text-white' 
                                  : 'bg-tan-200 text-tan-800'
                                : isOwn 
                                  ? 'bg-navy-800 text-gray-300 hover:bg-navy-700' 
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            <span>{emoji}</span>
                            <span>{users.length}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    
                    <p className={`text-xs mt-1 ${isOwn ? 'text-gray-300' : 'text-gray-400'}`}>
                      {formatTime(message.createdAt)}
                    </p>
                  </div>
                  
                  {/* Action buttons - reply, reaction and delete */}
                  <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {/* Reply Button */}
                    <button
                      onClick={() => {
                        setReplyTo({
                          id: message.id,
                          userName: message.userName,
                          content: message.content && message.content !== 'Sent an attachment'
                            ? message.content
                            : (message.attachmentType ? `[${message.attachmentType}]` : '')
                        });
                        inputRef.current?.focus();
                      }}
                      className="p-1 text-gray-400 hover:text-navy-700 transition-colors"
                      title="Reply"
                      data-testid={`group-reply-btn-${message.id}`}
                    >
                      <Reply className="w-4 h-4" />
                    </button>
                    {/* Add Reaction Button */}
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          className="p-1 text-gray-400 hover:text-tan-600 transition-colors"
                          title="Add reaction"
                        >
                          <SmilePlus className="w-4 h-4" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-2" side={isOwn ? "left" : "right"}>
                        <div className="flex gap-1">
                          {REACTION_EMOJIS.map((emoji) => (
                            <button
                              key={emoji}
                              onClick={() => handleReaction(message.id, emoji)}
                              className="text-xl hover:scale-125 transition-transform p-1"
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                    
                    {/* Delete button */}
                    {canDeleteMessage(message) && (
                      <button
                        onClick={() => handleDeleteMessage(message.id)}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                        title="Delete message"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="p-4 bg-white border-t sticky bottom-0">
        {/* Reply Preview */}
        {replyTo && (
          <div className="mb-3 flex items-center gap-2 bg-gray-100 rounded-xl px-3 py-2 border-l-4 border-navy-700" data-testid="group-reply-preview">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-navy-700">Replying to {replyTo.userName}</p>
              <p className="text-xs text-gray-500 truncate">{replyTo.content}</p>
            </div>
            <button type="button" onClick={() => setReplyTo(null)} className="p-1 text-gray-400 hover:text-red-500" data-testid="group-cancel-reply">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        {/* Attachment Preview */}
        {attachment && (
          <div className="mb-3 p-2 bg-gray-100 rounded-xl flex items-center gap-3">
            {attachment.type === 'image' ? (
              <img src={attachment.url} alt="Attachment" className="w-12 h-12 rounded-lg object-cover" />
            ) : attachment.type === 'video' ? (
              <div className="w-12 h-12 rounded-lg bg-navy-100 flex items-center justify-center">
                <Video className="w-6 h-6 text-navy-600" />
              </div>
            ) : (
              <div className="w-12 h-12 rounded-lg bg-tan-100 flex items-center justify-center">
                <FileText className="w-6 h-6 text-tan-600" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-navy-900 truncate">{attachment.name}</p>
              <p className="text-xs text-gray-500">Ready to send</p>
            </div>
            <button
              type="button"
              onClick={clearAttachment}
              className="p-1 text-gray-400 hover:text-red-500"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        )}
        
        <form onSubmit={handleSendMessage} className="flex gap-2 items-end">
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
            className="hidden"
          />
          
          {/* Attachment button */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="rounded-full w-10 h-10 text-gray-500 hover:text-navy-900 hover:bg-gray-100 flex-shrink-0"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || sending}
          >
            {uploading ? (
              <div className="relative">
                <Loader2 className="w-5 h-5 animate-spin" />
                {uploadProgress > 0 && (
                  <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs font-medium text-navy-900 bg-white px-1 rounded">
                    {uploadProgress}%
                  </span>
                )}
              </div>
            ) : (
              <Paperclip className="w-5 h-5" />
            )}
          </Button>
          
          <div className="flex-1">
            <textarea
              ref={inputRef}
              value={newMessage}
              onChange={(e) => {
                setNewMessage(e.target.value);
                // Auto-resize textarea
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (newMessage.trim() || attachment) {
                    handleSendMessage(e);
                  }
                }
              }}
              placeholder={attachment ? "Add a caption..." : "Type a message..."}
              className="w-full px-4 py-2 border border-gray-200 rounded-2xl resize-none overflow-hidden focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent"
              style={{ minHeight: '44px', maxHeight: '120px' }}
              disabled={sending}
              rows={1}
              data-testid="message-input"
            />
            <p className="text-xs text-gray-400 mt-1 text-center">Press Enter to send, Shift+Enter for new line</p>
          </div>
          <Button
            type="submit"
            size="icon"
            className="bg-navy-900 hover:bg-navy-800 rounded-full w-12 h-12 flex-shrink-0"
            disabled={(!newMessage.trim() && !attachment) || sending}
            data-testid="send-btn"
          >
            {sending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </Button>
        </form>
        <p className="text-xs text-gray-400 text-center mt-1">Press Shift+Enter for new line</p>
      </div>

      {/* Image Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-full"
            onClick={() => setSelectedImage(null)}
          >
            <X className="w-8 h-8" />
          </button>
          <img 
            src={selectedImage} 
            alt="Full size" 
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default GroupChatPage;
